import { useContext, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Box,
  alpha,
  Stack,
  lighten,
  Divider,
  IconButton,
  Tooltip,
  styled,
  useTheme,
  Button,
} from '@mui/material'
import MenuTwoToneIcon from '@mui/icons-material/MenuTwoTone'
import ChevronRightTwoToneIcon from '@mui/icons-material/ChevronRightTwoTone'
import { SidebarContext } from 'src/contexts/SidebarContext'
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'

import HeaderButtons from './Buttons'
import HeaderMenu from './Menu'

const MURAL_KEYS = [
  'app_config', 'mural_layouts', 'mapsConfigs', 'currentMapId', 'mapsEditSession', 'mapaEstadoFundos',
  'previsaoConfigs', 'currentPrevisaoId', 'previsaoEditSession', 'mareConfigs',
  'conteudoConfigs', 'conteudoActiveStates', 'tipoLayouts', 'tipoVinheta', 'conteudoFormatting',
  'currentConteudoId', 'menuPosition', 'mapType',
]

function getBackup() {
  try {
    return JSON.parse(localStorage.getItem('__support_backup__') || 'null')
  } catch {
    return null
  }
}

const HeaderWrapper = styled(Box)(
  ({ theme }) => `
        height: ${theme.header.height};
        color: ${theme.header.textColor};
        padding: ${theme.spacing(0, 2)};
        right: 0;
        z-index: 6;
        background-color: ${alpha(theme.header.background, 0.95)};
        backdrop-filter: blur(3px);
        position: fixed;
        justify-content: space-between;
        width: 100%;
        @media (min-width: ${theme.breakpoints.values.lg}px) {
            left: ${theme.sidebar.width};
            width: auto;
        }
`
)

const PROMO_WIDTH = '440px';

function Header() {
  const { sidebarToggle, toggleSidebar, sidebarCollapsed, toggleCollapse } = useContext(SidebarContext)
  const theme = useTheme()
  const isLoggedIn = !!localStorage.getItem('accessToken')
  const navigate = useNavigate()
  const [backup, setBackup] = useState(getBackup)

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === '__support_backup__') setBackup(getBackup())
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const handleEndSession = () => {
    MURAL_KEYS.forEach((key) => localStorage.removeItem(key))
    if (backup?.muralKeys) {
      Object.entries(backup.muralKeys).forEach(([key, value]) => {
        localStorage.setItem(key, value)
      })
    }
    localStorage.setItem('app-token', backup.token)
    localStorage.setItem('accessToken', backup.token)
    localStorage.setItem('user', backup.user)
    localStorage.removeItem('__support_backup__')
    window.location.href = '/extended-sidebar/menu/home'
  }

  const supportMode = !!backup

  return (
    <HeaderWrapper
      display="flex"
      alignItems="center"
      sx={{
        ...(supportMode && {
          backgroundColor: '#d84315 !important',
          backdropFilter: 'none',
          color: '#fff',
        }),
        [theme.breakpoints.up('lg')]: {
          left: sidebarCollapsed ? 0 : !isLoggedIn ? PROMO_WIDTH : undefined,
          width: sidebarCollapsed ? '100%' : !isLoggedIn ? `calc(100% - ${PROMO_WIDTH})` : undefined,
          transition: 'left 0.2s ease, width 0.2s ease',
        },
        boxShadow:
          theme.palette.mode === 'dark'
            ? `0 1px 0 ${alpha(
                lighten(theme.colors.primary.main, 0.7),
                0.15
              )}, 0px 2px 8px -3px rgba(0, 0, 0, 0.2), 0px 5px 22px -4px rgba(0, 0, 0, .1)`
            : `0px 2px 8px -3px ${alpha(
                theme.colors.alpha.black[100],
                0.2
              )}, 0px 5px 22px -4px ${alpha(
                theme.colors.alpha.black[100],
                0.1
              )}`,
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        {sidebarCollapsed && (
          <Box
            component="span"
            sx={{ display: { lg: 'inline-block', xs: 'none' } }}
          >
            <Tooltip arrow title="Abrir menu">
              <IconButton
                onClick={toggleCollapse}
                sx={{ color: supportMode ? '#fff' : 'primary.main' }}
              >
                <ChevronRightTwoToneIcon fontSize="small" />
              </IconButton>
            </Tooltip>

          </Box>
        )}
        <Stack
          direction="row"
          divider={<Divider orientation="vertical" flexItem />}
          alignItems="center"
          spacing={2}
        >
          {/* <HeaderSearch /> */}
          <HeaderMenu />
        </Stack>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        {supportMode && (
          <>
            <SupportAgentIcon fontSize="small" sx={{ color: '#fff', opacity: 0.85 }} />
            <Box
              component="span"
              sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 600, mr: 1, whiteSpace: 'nowrap' }}
            >
              Suporte:{' '}
              {(() => {
                try {
                  const u = JSON.parse(localStorage.getItem('user') || '{}')
                  return u.name || u.email || 'Cliente'
                } catch {
                  return 'Cliente'
                }
              })()}
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={handleEndSession}
              sx={{
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.6)',
                mr: 1,
                '&:hover': {
                  borderColor: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.15)',
                },
              }}
            >
              Encerrar sessão
            </Button>
          </>
        )}
        {!isLoggedIn && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => navigate('/account/login')}
            sx={{ mr: 1, fontWeight: 600 }}
          >
            Entrar
          </Button>
        )}
        <HeaderButtons />
        {/* <HeaderUserbox /> */}
        <Box
          component="span"
          sx={{
            ml: 2,
            display: { lg: 'none', xs: 'inline-block' },
          }}
        >
          <Tooltip arrow title="Menu">
            <IconButton
              onClick={toggleSidebar}
              sx={{ color: supportMode ? '#fff' : 'primary.main' }}
            >
              {!sidebarToggle ? (
                <MenuTwoToneIcon fontSize="small" />
              ) : (
                <CloseTwoToneIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </HeaderWrapper>
  )
}

export default Header
